import m, {logger} from '@bitstillery/mithril'
import {copy_object, debounce, plausible_event} from '@bitstillery/common/lib/utils'
import {set_language} from '@bitstillery/common/lib/i18n'
import {$t, api, events, notifier, store, telemetry} from '@bitstillery/common/app'
import {PricePreference} from '@bitstillery/common/api/fact2server'
import {PlausibleEvent} from '@bitstillery/common/types/utils'

import {get_support} from '@bitstillery/common/lib/support'

import {init_tawk, refresh_tawk_visitor_after_login, toggle_tawk} from '@/lib/tawk'
import {$s} from '@/app'
import {temporary} from '@/lib/state'

// Cookie name for syncing token from localStorage
const TOKEN_COOKIE_NAME = 'portal_token'

/**
 * Sync token to cookie when saved to localStorage
 * This allows SSR to read the token from cookies
 */
function sync_token_to_cookie(token: string | null): void {
    if (typeof document === 'undefined') {
        // Not in browser, skip cookie sync
        return
    }

    // Secure keeps the bearer token off the wire on a stray http:// hit (a bookmark, a typed URL) —
    // the redirect to https comes too late, the request already carried the header. Conditional so
    // plain-http dev hosts can still set it at all.
    const secure = location.protocol === 'https:' ? '; Secure' : ''

    if (token) {
        // Set cookie with token
        // HttpOnly: false so client can read/write it
        // SameSite: Lax for CSRF protection
        // Path: / so it's available for all routes
        const expires = new Date()
        expires.setTime(expires.getTime() + 1000 * 60 * 60 * 24 * 365) // 1 year
        document.cookie = `${TOKEN_COOKIE_NAME}=${encodeURIComponent(token)}; Path=/; SameSite=Lax${secure}; Expires=${expires.toUTCString()}`
    } else {
        // Clear cookie by setting it to expire in the past
        document.cookie = `${TOKEN_COOKIE_NAME}=; Path=/; SameSite=Lax${secure}; Expires=Thu, 01 Jan 1970 00:00:00 GMT`
    }
}

export function bind() {
    events.on('identity.logout', () => {
        // Finally, give Mithril some time to route to the logged-out view,
        // So we can safely remove the identity data.
        // Merge into existing state to preserve Mithril state structure ($relation_name etc.)
        const reset = copy_object(temporary).identity
        Object.assign($s.identity.user, reset.user)
        $s.identity.artkey = reset.artkey
        $s.identity.exp = reset.exp
    })
}

export async function fetch_contact(price_list_uuid: string) {
    const {result} = await api.get('portal/user/profile', {price_list_uuid})
    Object.assign($s.identity.user, result)

    void set_language($s.identity.user.language)
}

export function logout(force = false) {
    logger.debug('identity.logout', {module: 'identity'})

    plausible_event(PlausibleEvent.ACCESS, {
        props: {
            company_type: $s.identity.user.company_type,
            currency: $s.identity.user.currency,
            customs_status: $s.identity.user.customs_visibility,
            language: $s.identity.user.language,
            price_preference: $s.identity.user.price_preference,
            type: 'access_logout',
        },
    })

    // Change minimal profile data to indicate we are logged out first.
    $s.identity.token = null
    $s.cart.artkey = null
    $s.support.manager.email_address = ''
    $s.support.manager.mobile_telephone_number = ''
    $s.support.manager.name = ''
    $s.support.manager.phone_number = ''
    $s.support.contact = get_support()
    toggle_tawk(false)
    // Sync token removal to cookie
    sync_token_to_cookie(null)
    // Then route to the public landing; giving logged-in views room to close.
    void m.route.set('/')
    telemetry.set_user(undefined)
    void store.save()

    if (force) {
        notifier.notify($t('landing.force_logout', 'warning'))
    }

    events.emit('identity.logout')
}

/** Loads the profile behind the current token. Returns false when the token was rejected. */
export async function load_user(): Promise<boolean> {
    logger.debug('load_user', {module: 'identity'})

    const [account_response, profile_response] = await Promise.all([
        api.get(`portal/account/${process.env.MSI_THEME}`),
        api.get('portal/user/profile'),
    ])

    // A rejected token resolves with the error body as `result`; assigning it would corrupt the
    // profile and leave the app in a half-authenticated state.
    if (!account_response.success || !profile_response.success) {
        logger.debug('load_user rejected', {module: 'identity'})
        return false
    }

    Object.assign($s.identity.user, profile_response.result)

    plausible_event(PlausibleEvent.VISIT, {
        props: {
            company_type: $s.identity.user.company_type,
            currency: $s.identity.user.currency,
            customs_status: $s.identity.user.customs_visibility,
            language: $s.identity.user.language,
            price_preference: $s.identity.user.price_preference,
        },
    })

    telemetry.set_user({artkey: profile_response.result.artkey})

    $s.cart.show_bottles = $s.identity.user.price_preference === PricePreference.Bottle
    $s.portal.$account.value = account_response.result
    const support_manager = $s.identity.user.sales_manager
    // Make the email address account-specific. The phone number is a direct
    // Number to the support manager and is not account-specific. Therefor,
    // We use the account's generic number here.
    support_manager.email_address = `${support_manager.email_address.replace(/@.*/su, '')}@${$s.portal.account.domain}`
    support_manager.phone_number = $s.portal.account.telephone_number

    // Update signal values - support.manager is already initialized in state templates
    $s.support.manager.email_address = support_manager.email_address
    $s.support.manager.mobile_telephone_number = support_manager.mobile_telephone_number || ''
    $s.support.manager.name = support_manager.name
    $s.support.manager.phone_number = support_manager.phone_number

    $s.support.contact = get_support()

    void store.save()
    return true
}

/**
 * Drop a token the backend no longer accepts, without the routing and notification side effects
 * of `logout` — used before the first render, when there is nothing yet to route away from.
 */
export function discard_token() {
    $s.identity.token = null
    sync_token_to_cookie(null)
    void store.save()
}

/** Returns false when the token was rejected; the caller decides how to recover. */
export async function new_token(token: string): Promise<boolean> {
    logger.debug(`loading ${token ? 'existing' : 'new'} token`, {module: 'identity'})
    $s.identity.token = token
    // Sync token to cookie so SSR can read it
    sync_token_to_cookie(token)
    // (!) Load profile-related data first, before signalling that we're logged in.
    // Otherwise code has to be needlessly sprinkled around in components, checking for
    // Logged in status.
    if (!(await load_user())) {
        return false
    }
    events.emit('identity.login', $s.identity)

    init_tawk()
    refresh_tawk_visitor_after_login()
    return true
}

export const logout_debounced = debounce(100, () => {
    // The timer outlives the SSR response, so firing here would route and notify against whichever
    // request's store is in flight by then. A token the server rejects is handled inline instead.
    if (globalThis.__SSR_MODE__) {
        return
    }
    logout()
    notifier.notify($t('landing.force_logout', 'warning'))
})
